import styled from 'styled-components/macro'

import { Button, DotsLoader, Icon, Layout, Text, Tooltip } from '@loadsmart/miranda-react'

import { type AgnosticDocument, DocumentStatus, type DocumentType } from '../../domain/Document'
import { type InvoiceStatus } from '../../domain/Invoice'
import {
  formatDocumentDate,
  formatDocumentValue,
  getDocumentTitleProps,
} from '../../presenters/documentPresenter'
import { StatusIndicator } from '../tracking/StatusIndicator'
import { DocumentIconStatus } from './DocumentIconStatus'

type BaseDocumentItemProps = Readonly<AgnosticDocument> & {
  readonly onClick: () => void
  readonly loading?: boolean
  readonly disabled?: boolean
  readonly auditEnabled?: boolean
}

type InvoiceDocumentItemProps = BaseDocumentItemProps & {
  readonly status: InvoiceStatus
  readonly type: DocumentType.Invoice
}

type OtherDocumentItemProps = BaseDocumentItemProps & {
  readonly status?: DocumentStatus | null
  readonly type:
    | DocumentType.BOL
    | DocumentType.POD
    | DocumentType.Other
    | DocumentType.RateConfirmation
    | DocumentType.WeightCertificate
    | DocumentType.InspectionCertificate
}

export type DocumentItemProps = OtherDocumentItemProps | InvoiceDocumentItemProps

const StyledText = styled(Text)<{ lineThrough: boolean }>`
  --m-text-max-width: 10rem;
  ${({ lineThrough }) =>
    lineThrough &&
    `
    text-decoration: line-through;
  `}
`

const StyledGroup = styled(Layout.Group)`
  flex-wrap: nowrap;
`

export function DocumentItem({
  title,
  createdAt,
  onClick,
  type,
  status,
  loading = false,
  auditEnabled = false,
}: DocumentItemProps) {
  const titleProps = getDocumentTitleProps(status)
  const isDenied = status === DocumentStatus.Denied
  const isPending = status === DocumentStatus.PendingReview

  return (
    <StyledGroup justify="space-between" align="center">
      <Layout.Group justify="space-between">
        <Layout.Stack gap="spacing-1">
          <Layout.Group align="center" gap="spacing-0-5">
            <DocumentIconStatus
              data-testid="document-icon-status"
              status={auditEnabled ? status : null}
            />
            <StyledText variant="body-md-bold" ellipsized lineThrough={isDenied} {...titleProps}>
              {formatDocumentValue(title, type)}
            </StyledText>
          </Layout.Group>
          <Text variant="body-sm" color="color-neutral-60">
            {formatDocumentDate(createdAt)}
          </Text>
        </Layout.Stack>
      </Layout.Group>
      <Layout.Group justify="flex-end" gap="spacing-4" align="center">
        {auditEnabled && isPending && (
          <Layout.Group align="center" gap="spacing-2">
            <StatusIndicator color="color-neutral-40" />
            <Text variant="body-sm" color="color-neutral-60">
              AWAITING REVIEW
            </Text>
          </Layout.Group>
        )}
        <Layout.Stack>
          <Tooltip message="Preview" placement="top">
            <Button
              data-testid={`view-document-${type}`}
              aria-label={`view document ${type}`}
              variant="icon-secondary"
              size="small"
              onClick={onClick}
              loading={loading}
            >
              {loading ? (
                <DotsLoader data-testid={`loading-document-${type}`} size="4px" />
              ) : (
                <Icon size="small" name="eye" />
              )}
            </Button>
          </Tooltip>
        </Layout.Stack>
      </Layout.Group>
    </StyledGroup>
  )
}
